import React, { useState, useRef, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  IconButton,
  Chip,
  Alert,
  LinearProgress,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DataGrid, itIT } from '@mui/x-data-grid';
import {
  Send as SendIcon,
  Refresh as RefreshIcon,
  Visibility as PreviewIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as QueuedIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { it } from 'date-fns/locale';
import { format } from 'date-fns';
import { invoicesApi } from '../../services/api';
import EmailPreviewDialog from './EmailPreviewDialog';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value || 0);
};

const formatDate = (date) => {
  if (!date) return '-';
  try {
    return format(new Date(date), 'dd/MM/yyyy');
  } catch {
    return '-';
  }
};

const BulkEmailDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Filtri
  const [dataEmissioneDa, setDataEmissioneDa] = useState(null);
  const [dataEmissioneA, setDataEmissioneA] = useState(null);
  const [statoFattura, setStatoFattura] = useState('all');
  const [includeAlreadySent, setIncludeAlreadySent] = useState(false);
  
  // Dati
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Invio
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState({ total: 0, current: 0, sent: 0, failed: 0, skipped: 0 });
  const [sendResults, setSendResults] = useState([]);
  const abortRef = useRef(false);
  
  // Preview email
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewInvoiceId, setPreviewInvoiceId] = useState(null);

  // Carica anteprima fatture
  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSendResults([]);
    
    try {
      const filters = {
        include_already_sent: includeAlreadySent,
        stato_fattura: statoFattura,
      };
      
      if (dataEmissioneDa) {
        filters.data_emissione_da = format(dataEmissioneDa, 'yyyy-MM-dd');
      }
      if (dataEmissioneA) {
        filters.data_emissione_a = format(dataEmissioneA, 'yyyy-MM-dd');
      }
      
      const response = await invoicesApi.sendBulkPreview(filters);
      const data = response.data?.data || {};
      
      setInvoices(data.invoices || []);
      setStats(data.stats || null);
      
      // Pre-seleziona solo quelle inviabili
      const sendableIds = (data.invoices || [])
        .filter(inv => inv.can_send)
        .map(inv => inv.invoice_id);
      setSelectedIds(sendableIds);
      
    } catch (e) {
      setError(e.response?.data?.message || 'Errore nel caricamento delle fatture');
      setInvoices([]);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [dataEmissioneDa, dataEmissioneA, statoFattura, includeAlreadySent]);

  // Invia selezionate
  const handleSendSelected = async () => {
    if (selectedIds.length === 0) return;
    
    // Filtra solo quelle che possono essere inviate
    const toSend = invoices
      .filter(inv => selectedIds.includes(inv.invoice_id) && inv.can_send)
      .map(inv => inv.invoice_id);
    
    if (toSend.length === 0) {
      setError('Nessuna fattura selezionata può essere inviata');
      return;
    }
    
    await executeSend(toSend);
  };

  // Invia tutte (solo quelle inviabili)
  const handleSendAll = async () => {
    const sendable = invoices.filter(inv => inv.can_send).map(inv => inv.invoice_id);
    if (sendable.length === 0) {
      setError('Nessuna fattura può essere inviata');
      return;
    }
    
    await executeSend(sendable);
  };

  // Esegue invio con progress
  const executeSend = async (invoiceIds) => {
    setSending(true);
    setSendProgress({ total: invoiceIds.length, current: 0, sent: 0, failed: 0, skipped: 0 });
    setSendResults([]);
    abortRef.current = false;
    setError(null);

    try {
      // Usa il nuovo endpoint che gestisce rate limiting lato server
      const response = await invoicesApi.sendBulkExecute({
        invoice_ids: invoiceIds,
        delay_ms: 200 // Rate limit: 5 email/secondo
      });
      
      const data = response.data?.data || {};
      setSendResults(data.results || []);
      setSendProgress({
        total: data.summary?.total || invoiceIds.length,
        current: data.summary?.total || invoiceIds.length,
        sent: data.summary?.sent || 0,
        failed: data.summary?.failed || 0,
        skipped: data.summary?.skipped || 0,
        queued: data.summary?.queued || 0,
      });
      
      // Aggiorna la lista per riflettere lo stato invio
      await loadPreview();
      
    } catch (e) {
      setError(e.response?.data?.message || 'Errore durante l\'invio');
    } finally {
      setSending(false);
    }
  };

  // Interrompi invio
  const handleAbort = () => {
    abortRef.current = true;
  };

  // Apri anteprima email
  const handleOpenPreview = (invoiceId) => {
    setPreviewInvoiceId(invoiceId);
    setPreviewOpen(true);
  };

  // Invia singola dall'anteprima
  const handleSendFromPreview = async (invoiceId) => {
    setPreviewOpen(false);
    await executeSend([invoiceId]);
  };

  // Colonne DataGrid
  const columns = [
    {
      field: 'nuf',
      headerName: 'N° Fattura',
      width: isMobile ? 100 : 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {params.row.email_sent && (
            <Tooltip title={`Già inviata${params.row.email_sent_at ? ' il ' + formatDate(params.row.email_sent_at) : ''}`}>
              <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
            </Tooltip>
          )}
          <Typography fontWeight="medium">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: 'customer_name',
      headerName: 'Cliente',
      flex: 1,
      minWidth: isMobile ? 120 : 180,
    },
    ...(isMobile ? [] : [{
      field: 'email',
      headerName: 'Email',
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2" color={params.value ? 'inherit' : 'error'}>
          {params.value || 'N/D'}
        </Typography>
      ),
    }]),
    {
      field: 'f_emission',
      headerName: 'Emissione',
      width: isMobile ? 90 : 100,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      field: 'f_totale',
      headerName: 'Importo',
      width: isMobile ? 95 : 110,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (params) => formatCurrency(params.value),
    },
    {
      field: 'status',
      headerName: 'Stato',
      width: 140,
      renderCell: (params) => {
        const row = params.row;
        
        // Cerca risultato invio
        const result = sendResults.find(r => r.invoice_id === row.invoice_id);
        
        if (result) {
          if (result.status === 'sent') {
            return <Chip icon={<CheckCircleIcon />} label="Inviata" color="success" size="small" />;
          } else if (result.status === 'failed') {
            return (
              <Tooltip title={result.error || 'Errore'}>
                <Chip icon={<ErrorIcon />} label="Errore" color="error" size="small" />
              </Tooltip>
            );
          } else if (result.status === 'queued') {
            return <Chip icon={<QueuedIcon />} label="In coda" color="info" size="small" />;
          } else if (result.status === 'skipped') {
            return (
              <Tooltip title={result.reason || 'Saltata'}>
                <Chip icon={<BlockIcon />} label="Saltata" color="default" size="small" />
              </Tooltip>
            );
          }
        }
        
        // Stato pre-invio
        if (row.email_sent) {
          return <Chip label="Già inviata" color="default" size="small" variant="outlined" />;
        }
        if (!row.can_send) {
          return (
            <Tooltip title={row.exclusion_reason || 'Non inviabile'}>
              <Chip label="Esclusa" color="warning" size="small" variant="outlined" />
            </Tooltip>
          );
        }
        return <Chip label="Da inviare" color="primary" size="small" variant="outlined" />;
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="Anteprima email">
          <span>
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                handleOpenPreview(params.row.invoice_id);
              }}
              disabled={!params.row.email}
            >
              <PreviewIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  // Calcola statistiche selezione
  const selectedSendable = invoices.filter(
    inv => selectedIds.includes(inv.invoice_id) && inv.can_send
  );
  const selectedTotal = selectedSendable.reduce((sum, inv) => sum + (inv.f_totale || 0), 0);

  return (
    <>
      <Dialog 
        open={open} 
        onClose={sending ? undefined : onClose} 
        maxWidth="lg" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { minHeight: '80vh' } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon color="primary" />
              <Typography variant="h6">Invio Massivo Fatture via Email</Typography>
            </Box>
            {!sending && (
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Filtri */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Filtri
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <DatePicker
                  label="Data emissione da"
                  value={dataEmissioneDa}
                  onChange={setDataEmissioneDa}
                  slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                />
                <DatePicker
                  label="Data emissione a"
                  value={dataEmissioneA}
                  onChange={setDataEmissioneA}
                  slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Stato fattura</InputLabel>
                  <Select
                    value={statoFattura}
                    onChange={(e) => setStatoFattura(e.target.value)}
                    label="Stato fattura"
                  >
                    <MenuItem value="all">Tutti</MenuItem>
                    <MenuItem value="emessa">Emessa</MenuItem>
                    <MenuItem value="parziale">Parziale</MenuItem>
                    <MenuItem value="pagata">Pagata</MenuItem>
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeAlreadySent}
                      onChange={(e) => setIncludeAlreadySent(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Includi già inviate"
                />
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                  onClick={loadPreview}
                  disabled={loading || sending}
                >
                  Cerca
                </Button>
              </Box>
            </LocalizationProvider>
          </Paper>

          {/* Errore */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Progress invio */}
          {sending && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2">
                  Invio in corso... {sendProgress.current} / {sendProgress.total}
                </Typography>
                <Button 
                  variant="outlined" 
                  color="error" 
                  size="small"
                  onClick={handleAbort}
                >
                  Interrompi
                </Button>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={(sendProgress.current / sendProgress.total) * 100} 
                sx={{ mb: 1, height: 8, borderRadius: 1 }}
              />
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="success.main">
                  ✓ Inviate: {sendProgress.sent}
                </Typography>
                <Typography variant="body2" color="info.main">
                  ⏳ In coda: {sendProgress.queued || 0}
                </Typography>
                <Typography variant="body2" color="error.main">
                  ✗ Fallite: {sendProgress.failed}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ⊘ Saltate: {sendProgress.skipped}
                </Typography>
              </Box>
            </Paper>
          )}

          {/* Statistiche */}
          {stats && !sending && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>{stats.total}</strong> fatture trovate •{' '}
              <strong>{stats.sendable}</strong> inviabili •{' '}
              <strong>{stats.excluded}</strong> escluse •{' '}
              <strong>{stats.already_sent}</strong> già inviate •{' '}
              Totale: <strong>{formatCurrency(stats.total_amount)}</strong>
            </Alert>
          )}

          {/* Selezione info */}
          {selectedIds.length > 0 && !sending && (
            <Alert severity="success" sx={{ mb: 2 }} icon={<SendIcon />}>
              <strong>{selectedSendable.length}</strong> fatture selezionate per l'invio •{' '}
              Importo totale: <strong>{formatCurrency(selectedTotal)}</strong>
            </Alert>
          )}

          {/* Risultati invio */}
          {sendResults.length > 0 && !sending && (
            <Alert 
              severity={sendProgress.failed > 0 ? 'warning' : 'success'} 
              sx={{ mb: 2 }}
            >
              Invio completato: {sendProgress.sent} inviate, {sendProgress.queued || 0} in coda, 
              {sendProgress.failed} fallite, {sendProgress.skipped} saltate
            </Alert>
          )}

          {/* DataGrid fatture */}
          <DataGrid
            rows={invoices}
            columns={columns}
            getRowId={(row) => row.invoice_id}
            loading={loading}
            checkboxSelection
            disableRowSelectionOnClick
            rowSelectionModel={selectedIds}
            onRowSelectionModelChange={(newSelection) => setSelectedIds(newSelection)}
            isRowSelectable={(params) => params.row.can_send}
            getRowClassName={(params) => {
              if (params.row.email_sent) return 'row-already-sent';
              if (!params.row.can_send) return 'row-cannot-send';
              return '';
            }}
            autoHeight
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
            sx={{ 
              border: 'none',
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
              },
              '& .row-already-sent': {
                bgcolor: 'action.hover',
                '&:hover': {
                  bgcolor: 'action.selected',
                },
              },
              '& .row-cannot-send': {
                opacity: 0.6,
              },
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={sending}>
            Chiudi
          </Button>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <Button
            variant="outlined"
            startIcon={<SendIcon />}
            onClick={handleSendSelected}
            disabled={selectedSendable.length === 0 || loading || sending}
          >
            Invia Selezionate ({selectedSendable.length})
          </Button>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleSendAll}
            disabled={!stats?.sendable || loading || sending}
          >
            Invia Tutte ({stats?.sendable || 0})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog anteprima email */}
      <EmailPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        invoiceId={previewInvoiceId}
        onSend={handleSendFromPreview}
      />
    </>
  );
};

export default BulkEmailDialog;
